/* Iron+Steel v3 SiteHeader behaviour.
   Layout switching at 860px is pure CSS; this file only handles state:
   mobile drawer, search panel + predictive search, desktop mega menus, bag and favorites badges.
   The cart drawer itself stays with the theme's <cart-items data-cart-drawer> (opened via [data-cart-drawer-toggle]). */

(() => {
  if (customElements.get('is-header')) return;

  const WISHLIST_KEY = 'is-wishlist';
  const CART_ENDPOINT = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* Shared favorites store. Later pieces (cards, product, wishlist page) read and write through this. */
  window.ISWishlist = window.ISWishlist || {
    read() {
      try {
        const v = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
        return Array.isArray(v) ? v : [];
      } catch (e) {
        return [];
      }
    },
    write(handles) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('is:wishlist:change', {detail: {handles}}));
    },
    count() {
      return this.read().length;
    },
    /* Variant chosen when the item was saved from a product page ({handle: variantId}). Grid cards save none. */
    variants() {
      try {
        const v = JSON.parse(localStorage.getItem(`${WISHLIST_KEY}-variants`) || '{}');
        return v && typeof v === 'object' ? v : {};
      } catch (e) {
        return {};
      }
    },
    setVariant(handle, id) {
      const map = this.variants();
      if (id) map[handle] = id;
      else delete map[handle];
      try {
        localStorage.setItem(`${WISHLIST_KEY}-variants`, JSON.stringify(map));
      } catch (e) {}
    },
  };

  /* Announce every AJAX cart mutation (theme, quick add, apps) as `is:cart:mutated`.
     Matched on the request URL, since Shopify redirects some endpoints (clear.js -> cart.js). */
  if (!window.__isCartHooked) {
    window.__isCartHooked = true;
    const announce = () => document.dispatchEvent(new CustomEvent('is:cart:mutated'));
    const urlOf = (input) => (typeof input === 'string' ? input : input && input.url) || '';

    const nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      const promise = nativeFetch.apply(this, arguments);
      if (CART_ENDPOINT.test(urlOf(input))) promise.then(announce, () => {});
      return promise;
    };

    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_ENDPOINT.test(String(url))) this.addEventListener('loadend', announce);
      return nativeOpen.apply(this, arguments);
    };
  }

  class ISHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-drawer]');
      this.burger = this.querySelector('[data-drawer-open]');
      this.searchPanel = this.querySelector('[data-search-panel]');
      this.searchToggle = this.querySelector('[data-search-toggle]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultHits = this.results ? this.results.innerHTML : '';
      this.bagBadge = this.querySelector('[data-bag-count]');
      this.bagLink = this.querySelector('[data-is-bag]');
      this.favBadge = this.querySelector('[data-wishlist-count]');
      this.megaTriggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
      this.openMega = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.renderFavorites = this.renderFavorites.bind(this);
      this.refreshBag = this.debounce(this.refreshBag.bind(this), 120);

      this.bindDrawer();
      this.bindSearch();
      this.bindMega();
      this.bindBadges();

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);

      // Expose the sticky header's height so other sticky bars (collection filters) sit under it.
      const group = this.closest('.is-header-group') || this;
      const setHeight = () => document.documentElement.style.setProperty('--is-header-h', `${group.offsetHeight}px`);
      setHeight();
      if ('ResizeObserver' in window) {
        this.heightObserver = new ResizeObserver(setHeight);
        this.heightObserver.observe(group);
      }
    }

    disconnectedCallback() {
      if (this.heightObserver) this.heightObserver.disconnect();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('is:wishlist:change', this.renderFavorites);
      window.removeEventListener('storage', this.renderFavorites);
      ['theme:cart:added', 'theme:cart:loaded', 'theme:cart:close'].forEach((e) => document.removeEventListener(e, this.refreshBag));
      document.removeEventListener('is:cart:mutated', this.refreshBag);
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onBreakpoint);
      this.unlockScroll();
    }

    /* ---------- Mobile drawer ---------- */
    bindDrawer() {
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
      if (this.burger) this.burger.addEventListener('click', () => this.openDrawer());

      // Expanding menu items. Siblings at the same level close, so the drawer never becomes a
      // wall of links; a nested level closes with its parent.
      this.querySelectorAll('[data-drawer-sub]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.parentElement.querySelectorAll(':scope > [data-drawer-sub]').forEach((sib) => {
            if (sib === btn) return;
            sib.setAttribute('aria-expanded', 'false');
            const p = document.getElementById(sib.getAttribute('aria-controls'));
            if (p) p.hidden = true;
          });
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
          if (open) {
            panel.querySelectorAll('[data-drawer-sub]').forEach((inner) => {
              inner.setAttribute('aria-expanded', 'false');
              const p = document.getElementById(inner.getAttribute('aria-controls'));
              if (p) p.hidden = true;
            });
          }
        });
      });

      // The drawer only exists below 860px (CSS). If the viewport grows past it, drop the open state.
      this.desktopQuery = window.matchMedia('(min-width: 860px)');
      this.onBreakpoint = (e) => {
        if (e.matches) this.closeDrawer(false);
        else this.closeMega();
      };
      this.desktopQuery.addEventListener('change', this.onBreakpoint);
    }

    openDrawer() {
      this.closeSearch(false);
      this.setAttribute('data-drawer-open', '');
      this.burger.setAttribute('aria-expanded', 'true');
      this.lockScroll();
      const first = this.drawer.querySelector('a, button');
      if (first) setTimeout(() => first.focus(), 60);
    }

    closeDrawer(restoreFocus = true) {
      if (!this.hasAttribute('data-drawer-open')) return;
      this.removeAttribute('data-drawer-open');
      this.burger.setAttribute('aria-expanded', 'false');
      this.unlockScroll();
      if (restoreFocus) this.burger.focus();
    }

    lockScroll() {
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      document.documentElement.style.overflow = '';
    }

    /* ---------- Search panel ---------- */
    bindSearch() {
      if (!this.searchPanel) return;
      this.searchToggle.addEventListener('click', () => (this.searchPanel.hidden ? this.openSearch() : this.closeSearch()));
      this.querySelector('[data-search-close]').addEventListener('click', () => this.closeSearch());
      this.searchInput.addEventListener('input', this.debounce(() => this.runSearch(), 220));
    }

    openSearch() {
      this.closeMega();
      this.closeDrawer(false);
      this.searchPanel.hidden = false;
      this.searchToggle.setAttribute('aria-expanded', 'true');
      this.searchInput.focus();
    }

    closeSearch(restoreFocus = true) {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.hidden = true;
      this.searchToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.searchToggle.focus();
    }

    async runSearch() {
      const q = this.searchInput.value.trim();
      if (this.searchAbort) this.searchAbort.abort();

      if (!q) {
        this.results.innerHTML = this.defaultHits;
        this.searchPanel.removeAttribute('aria-busy');
        return;
      }

      this.searchAbort = new AbortController();
      this.searchPanel.setAttribute('aria-busy', 'true');

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'is-search-hits',
      });

      try {
        const res = await fetch(`${this.dataset.predictiveUrl}?${params}`, {signal: this.searchAbort.signal});
        if (!res.ok) throw new Error(res.status);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const content = html.querySelector('#is-search-hits-content');
        if (content) this.results.innerHTML = content.innerHTML;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
      this.searchPanel.removeAttribute('aria-busy');
    }

    /* ---------- Desktop mega menus ---------- */
    bindMega() {
      this.megaTriggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.showMega(trigger));
        trigger.addEventListener('focus', () => this.showMega(trigger));
      });
      this.querySelectorAll('[data-mega-close]').forEach((link) => {
        link.addEventListener('mouseenter', () => this.closeMega());
        link.addEventListener('focus', () => this.closeMega());
      });
      this.addEventListener('mouseleave', () => this.closeMega());
      this.querySelectorAll('[data-mega]').forEach((panel) => {
        panel.addEventListener('focusout', (e) => {
          if (!this.contains(e.relatedTarget)) this.closeMega();
        });
      });
    }

    showMega(trigger) {
      if (!this.desktopQuery.matches) return;
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel || this.openMega === panel) return;
      this.closeMega();
      this.closeSearch(false);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.openMega = panel;
    }

    closeMega() {
      if (!this.openMega) return;
      this.openMega.hidden = true;
      this.megaTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.openMega = null;
    }

    /* ---------- Global handlers ---------- */
    onKeydown(e) {
      if (e.key !== 'Escape') return;
      if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
      else if (this.searchPanel && !this.searchPanel.hidden) this.closeSearch();
      else if (this.openMega) {
        const trigger = this.querySelector(`[aria-controls="${this.openMega.id}"]`);
        this.closeMega();
        if (trigger) trigger.focus();
      }
    }

    onDocClick(e) {
      if (this.searchPanel && !this.searchPanel.hidden && !this.contains(e.target)) this.closeSearch(false);
    }

    /* ---------- Badges ---------- */
    bindBadges() {
      this.renderFavorites();
      document.addEventListener('is:wishlist:change', this.renderFavorites);
      window.addEventListener('storage', this.renderFavorites);

      // Theme cart events, plus any AJAX cart call (theme, quick add, apps) seen by the browser.
      ['theme:cart:added', 'theme:cart:loaded', 'theme:cart:close'].forEach((e) => document.addEventListener(e, this.refreshBag));
      document.addEventListener('is:cart:mutated', this.refreshBag);
    }

    renderFavorites() {
      if (!this.favBadge) return;
      const n = window.ISWishlist.count();
      this.favBadge.textContent = n;
      this.favBadge.hidden = n === 0;
    }

    async refreshBag() {
      try {
        const res = await fetch(`${window.theme?.routes?.root || '/'}cart.js`, {headers: {Accept: 'application/json'}});
        const cart = await res.json();
        this.setBagCount(cart.item_count);
      } catch (e) {}
    }

    setBagCount(n) {
      if (!this.bagBadge) return;
      this.bagBadge.textContent = n;
      this.bagBadge.hidden = n === 0;
      this.bagLink.setAttribute('aria-label', `Bag, ${n} ${n === 1 ? 'item' : 'items'}`);
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    }
  }

  customElements.define('is-header', ISHeader);
})();
